// acneScarsRemovalData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const stdtest: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "STD Test in Dubai | Elite Body Home Polyclinic",
            "description": "Reliable, discreet STD testing in Dubai with same-day results. Our DHA-certified nurses provide confidential at-home or hotel testing for HIV, chlamydia, gonorrhea, syphilis, herpes and more. Free doctor consultation if positive.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/std-test-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "STD Test in Dubai | Elite Body Home Polyclinic",
                "description": "Reliable, discreet STD testing in Dubai with same-day results. Our DHA-certified nurses provide confidential at-home or hotel testing for HIV, chlamydia, gonorrhea, syphilis, herpes and more. Free doctor consultation if positive.",
                "url": "https://elitebodyhome.com/services/std-test-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-STD-Test.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-STD-Test.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Private STD Testing Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "STD Test in Dubai | Elite Body Home Polyclinic",
                "description": "Reliable, discreet STD testing in Dubai with same-day results. Our DHA-certified nurses provide confidential at-home or hotel testing for HIV, chlamydia, gonorrhea, syphilis, herpes and more. Free doctor consultation if positive.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-STD-Test.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "STD Test Dubai"],
        "hero": {
            "title": "Private STD Testing in Dubai",
            "subtitle": "",
            "description": "Why visit a clinic when you can get discreet STD testing in the comfort of your home or hotel? Elite Body Home offers confidential, accurate STD testing with same-day results. Our DHA-certified nurses come to your preferred location, ensuring complete privacy and professional care."
        },
        "howItWorks": {
            "title": "STD Testing Process",
            "description": "Our private STD testing service in Dubai is designed for maximum confidentiality while maintaining the highest standards of accuracy."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Book Appointment",
                    "desc": "Contact us via WhatsApp, phone, or our online form to schedule your test.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Nurse Visit",
                    "desc": "A DHA-certified nurse arrives at your home or hotel with all necessary equipment.",
                    "icon": "fa-solid fa-user-nurse"
                },
                {
                    "title": "Sample Collection",
                    "desc": "Quick and professional sample collection completed in 10-15 minutes.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Lab Processing",
                    "desc": "Samples sent to our CAP and ISO-certified laboratories for analysis.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "Receive Results",
                    "desc": "Get your electronic results the same day via email or WhatsApp.",
                    "icon": "fa-solid fa-envelope"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of At-Home STD Testing",
            "subheading": "Why choose private STD testing",
            "para": "Choosing at-home STD testing offers numerous advantages for your peace of mind and convenience:",
            "items": [
                {
                    "title": "Privacy",
                    "desc": "Complete confidentiality with testing in your own home or hotel.",
                    "icon": "fa-solid fa-user-secret"
                },
                {
                    "title": "Convenience",
                    "desc": "No queues or waiting rooms - tested at your preferred time.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "Quick Results",
                    "desc": "Same-day results available when samples are submitted in the morning.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Professional Care",
                    "desc": "DHA-certified nurses with expertise in discreet testing.",
                    "icon": "fa-solid fa-user-md"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Should Get Tested",
            "subheading": "",
            "para": "Our STD testing service is recommended for:",
            "items": [
                {
                    "title": "Sexually Active Individuals",
                    "desc": "Especially those with multiple partners.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "After Unprotected Sex",
                    "desc": "Anyone who had intercourse without protection.",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "New Relationships",
                    "desc": "Before becoming intimate with a new partner.",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "Symptomatic Individuals",
                    "desc": "Those experiencing unusual discharge, pain, or other symptoms.",
                    "icon": "fa-solid fa-exclamation-triangle"
                }
            ]
        },
        "preparationTips": {
            "heading": "How to Prepare",
            "subheading": "",
            "para": "For accurate test results, please follow these preparation guidelines:",
            "items": [
                {
                    "title": "Urine Sample",
                    "desc": "Hold urine for at least one hour before urine-based tests.",
                    "icon": "fa-solid fa-toilet"
                },
                {
                    "title": "Avoid Sexual Activity",
                    "desc": "Refrain from sex for 24-48 hours before testing.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Medication Disclosure",
                    "desc": "Inform your doctor about any antibiotics or medications.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Sexual History",
                    "desc": "Be prepared to discuss your sexual activity for proper guidance.",
                    "icon": "fa-solid fa-comment-medical"
                }
            ]
        },
        "results": {
            "heading": "Understanding Your Results",
            "subheading": "",
            "para": "We make STD testing straightforward and supportive:",
            "items": [
                {
                    "title": "Same-Day Results",
                    "desc": "Most results available within 24 hours when samples are submitted in the morning.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Secure Delivery",
                    "desc": "Results sent electronically via email or WhatsApp.",
                    "icon": "fa-solid fa-lock"
                },
                {
                    "title": "Doctor Consultation",
                    "desc": "Free consultation if your test is positive.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "Treatment Plans",
                    "desc": "Personalized treatment options if needed.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                }
            ]
        },
        "aftercareTips": {
            "heading": "After Your Test",
            "subheading": "",
            "para": "Depending on your results, we recommend:",
            "items": [
                {
                    "title": "Partner Notification",
                    "desc": "Discuss with your doctor about informing partners if positive.",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "Follow-up Testing",
                    "desc": "Some STDs require retesting after treatment.",
                    "icon": "fa-solid fa-arrow-rotate-right"
                },
                {
                    "title": "Prevention Strategies",
                    "desc": "Learn about safer sex practices and prevention.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "Emotional Support",
                    "desc": "We provide compassionate care throughout the process.",
                    "icon": "fa-solid fa-hands-holding-heart"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How accurate are your STD tests?",
                    "answer": "All our tests are performed in CAP and ISO-certified laboratories using the most accurate testing methods available."
                },
                {
                    "question": "What STDs do you test for?",
                    "answer": "We offer comprehensive testing for HIV, chlamydia, gonorrhea, syphilis, herpes, hepatitis B and C, HPV, trichomoniasis, and more through various testing packages."
                },
                {
                    "question": "Is the testing really confidential?",
                    "answer": "Yes, we guarantee complete confidentiality. Our nurses are trained in discreet service, and results are only shared with you via secure channels."
                },
                {
                    "question": "What if my test is positive?",
                    "answer": "We provide free doctor consultation and will develop a personalized treatment plan for you if your results are positive."
                },
                {
                    "question": "Do you offer pre-marital STD screening?",
                    "answer": "Yes, we provide comprehensive pre-marital STD screening packages in Dubai."
                },
                {
                    "question": "Can I get tested at my hotel?",
                    "answer": "Absolutely. We provide testing at any private location in Dubai including hotels, residences, and offices."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/std-test-dubai-large.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 356,
            "title": "Comprehensive STD Test Package",
            "price": 0,
            "description": "Complete STD screening performed in the privacy of your home or hotel. Includes testing for HIV, chlamydia, gonorrhea, syphilis, herpes and more. Conducted by DHA-certified nurses with results reviewed by licensed physicians."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "فحص الأمراض المنقولة جنسيًا في دبي | عيادات ايليت بودي هوم",
            "description": "فحوصات أمراض منقولة جنسيًا موثوقة وسرية في دبي مع نتائج في نفس اليوم. ممرضات معتمدات من هيئة الصحة بدبي تقدم فحوصات سرية في المنزل أو الفندق لفيروس نقص المناعة البشرية، الكلاميديا، السيلان، الزهري، الهربس والمزيد. استشارة طبية مجانية إذا كانت النتيجة إيجابية.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/std-test-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "فحص الأمراض المنقولة جنسيًا في دبي | عيادات ايليت بودي هوم",
                "description": "فحوصات أمراض منقولة جنسيًا موثوقة وسرية في دبي مع نتائج في نفس اليوم. ممرضات معتمدات من هيئة الصحة بدبي تقدم فحوصات سرية في المنزل أو الفندق لفيروس نقص المناعة البشرية، الكلاميديا، السيلان، الزهري، الهربس والمزيد. استشارة طبية مجانية إذا كانت النتيجة إيجابية.",
                "url": "https://elitebodyhome.com/ar/services/std-test-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-STD-Test.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-STD-Test.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "فحص الأمراض المنقولة جنسيًا الخاص دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "فحص الأمراض المنقولة جنسيًا في دبي | عيادات ايليت بودي هوم",
                "description": "فحوصات أمراض منقولة جنسيًا موثوقة وسرية في دبي مع نتائج في نفس اليوم. ممرضات معتمدات من هيئة الصحة بدبي تقدم فحوصات سرية في المنزل أو الفندق لفيروس نقص المناعة البشرية، الكلاميديا، السيلان، الزهري، الهربس والمزيد. استشارة طبية مجانية إذا كانت النتيجة إيجابية.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-STD-Test.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "فحص الأمراض المنقولة جنسيًا دبي"],
        "hero": {
            "title": "فحص الأمراض المنقولة جنسيًا الخاص في دبي",
            "subtitle": "",
            "description": "لماذا تزور العيادة بينما يمكنك إجراء فحص الأمراض المنقولة جنسيًا في راحة منزلك أو فندقك؟ تقدم عيادات ايليت بودي هوم فحوصات سرية ودقيقة للأمراض المنقولة جنسيًا مع نتائج في نفس اليوم. تأتي ممرضاتنا المعتمدة من هيئة الصحة بدبي إلى موقعك المفضل، مما يضمن الخصوصية الكاملة والرعاية المهنية."
        },
        "howItWorks": {
            "title": "عملية فحص الأمراض المنقولة جنسيًا",
            "description": "تم تصميم خدمة الفحص الخاصة للأمراض المنقولة جنسيًا في دبي لتوفير أقصى درجات السرية مع الحفاظ على أعلى معايير الدقة."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "حجز الموعد",
                    "desc": "اتصل بنا عبر الواتساب أو الهاتف أو استخدم النموذج عبر الإنترنت لتحديد موعد الفحص.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "زيارة الممرضة",
                    "desc": "تصل ممرضة معتمدة من هيئة الصحة بدبي إلى منزلك أو فندقك مع جميع المعدات اللازمة.",
                    "icon": "fa-solid fa-user-nurse"
                },
                {
                    "title": "سحب العينة",
                    "desc": "سحب عينات سريع ومحترف يتم إكماله في 10-15 دقيقة.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "معالجة العينة",
                    "desc": "إرسال العينات إلى مختبراتنا المعتمدة من الكلية الأمريكية لعلماء الأمراض والمنظمة الدولية للتوحيد القياسي للتحليل.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "استلام النتائج",
                    "desc": "احصل على نتائجك الإلكترونية في نفس اليوم عبر البريد الإلكتروني أو الواتساب.",
                    "icon": "fa-solid fa-envelope"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد فحص الأمراض المنقولة جنسيًا في المنزل",
            "subheading": "لماذا تختار الفحص الخاص للأمراض المنقولة جنسيًا",
            "para": "يقدم اختيار الفحص المنزلي للأمراض المنقولة جنسيًا العديد من المزايا لراحتك وطمأنينتك:",
            "items": [
                {
                    "title": "الخصوصية",
                    "desc": "سرية كاملة مع الفحص في منزلك أو فندقك.",
                    "icon": "fa-solid fa-user-secret"
                },
                {
                    "title": "الراحة",
                    "desc": "لا طوابير أو غرف انتظار - يتم الفحص في الوقت الذي يناسبك.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "نتائج سريعة",
                    "desc": "نتائج في نفس اليوم متاحة عند تقديم العينات في الصباح.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "رعاية مهنية",
                    "desc": "ممرضات معتمدة من هيئة الصحة بدبي مع خبرة في الفحوصات السرية.",
                    "icon": "fa-solid fa-user-md"
                }
            ]
        },
        "eligibility": {
            "heading": "من يجب أن يقوم بالفحص",
            "subheading": "",
            "para": "نوصي بخدمة فحص الأمراض المنقولة جنسيًا لـ:",
            "items": [
                {
                    "title": "الأشخاص النشطون جنسيًا",
                    "desc": "خاصةً من لديهم شركاء متعددون.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "بعد ممارسة الجنس دون وقاية",
                    "desc": "أي شخص مارس الجماع بدون حماية.",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "العلاقات الجديدة",
                    "desc": "قبل البدء في علاقة حميمة مع شريك جديد.",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "الأشخاص الذين يعانون من أعراض",
                    "desc": "من يعانون من إفرازات غير طبيعية، ألم، أو أعراض أخرى.",
                    "icon": "fa-solid fa-exclamation-triangle"
                }
            ]
        },
        "preparationTips": {
            "heading": "كيفية التحضير",
            "subheading": "",
            "para": "لضمان دقة نتائج الفحص، يرجى اتباع إرشادات التحضير التالية:",
            "items": [
                {
                    "title": "عينة البول",
                    "desc": "الامتناع عن التبول لمدة ساعة على الأقل قبل اختبارات البول.",
                    "icon": "fa-solid fa-toilet"
                },
                {
                    "title": "تجنب النشاط الجنسي",
                    "desc": "الامتناع عن ممارسة الجنس لمدة 24-48 ساعة قبل الفحص.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "الكشف عن الأدوية",
                    "desc": "أخبر طبيبك عن أي مضادات حيوية أو أدوية تتناولها.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "التاريخ الجنسي",
                    "desc": "كن مستعدًا لمناقشة نشاطك الجنسي للحصول على التوجيه المناسب.",
                    "icon": "fa-solid fa-comment-medical"
                }
            ]
        },
        "results": {
            "heading": "فهم نتائجك",
            "subheading": "",
            "para": "نجعل فحص الأمراض المنقولة جنسيًا واضحًا وداعمًا:",
            "items": [
                {
                    "title": "نتائج في نفس اليوم",
                    "desc": "معظم النتائج متاحة خلال 24 ساعة عند تقديم العينات في الصباح.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تسليم آمن",
                    "desc": "إرسال النتائج إلكترونيًا عبر البريد الإلكتروني أو الواتساب.",
                    "icon": "fa-solid fa-lock"
                },
                {
                    "title": "استشارة الطبيب",
                    "desc": "استشارة طبية مجانية إذا كانت نتيجة الفحص إيجابية.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "خطط العلاج",
                    "desc": "خيارات علاج شخصية إذا لزم الأمر.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                }
            ]
        },
        "aftercareTips": {
            "heading": "بعد الفحص",
            "subheading": "",
            "para": "اعتمادًا على نتائجك، نوصي بـ:",
            "items": [
                {
                    "title": "إخطار الشريك",
                    "desc": "ناقش مع طبيبك إبلاغ الشركاء إذا كانت النتيجة إيجابية.",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "فحص المتابعة",
                    "desc": "بعض الأمراض المنقولة جنسيًا تتطلب إعادة الفحص بعد العلاج.",
                    "icon": "fa-solid fa-arrow-rotate-right"
                },
                {
                    "title": "استراتيجيات الوقاية",
                    "desc": "تعرف على ممارسات الجنس الآمن والوقاية.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "الدعم العاطفي",
                    "desc": "نقدم رعاية متعاطفة طوال العملية.",
                    "icon": "fa-solid fa-hands-holding-heart"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "ما مدى دقة فحوصات الأمراض المنقولة جنسيًا لديكم؟",
                    "answer": "جميع فحوصاتنا تجرى في مختبرات معتمدة من الكلية الأمريكية لعلماء الأمراض والمنظمة الدولية للتوحيد القياسي باستخدام أكثر طرق الفحص دقة المتاحة."
                },
                {
                    "question": "ما هي الأمراض المنقولة جنسيًا التي تفحصونها؟",
                    "answer": "نقدم فحوصات شاملة لفيروس نقص المناعة البشرية، الكلاميديا، السيلان، الزهري، الهربس، التهاب الكبد ب وج، فيروس الورم الحليمي البشري، داء المشعرات، والمزيد من خلال باقات فحص متنوعة."
                },
                {
                    "question": "هل الفحص سري حقًا؟",
                    "answer": "نعم، نضمن السرية الكاملة. ممرضاتنا مدربات على تقديم خدمة سرية، ولا يتم مشاركة النتائج إلا معك عبر قنوات آمنة."
                },
                {
                    "question": "ماذا لو كانت نتيجة فحصي إيجابية؟",
                    "answer": "نوفر استشارة طبية مجانية وسنضع خطة علاج شخصية لك إذا كانت نتائجك إيجابية."
                },
                {
                    "question": "هل تقدمون فحص ما قبل الزواج للأمراض المنقولة جنسيًا؟",
                    "answer": "نعم، نقدم باقات شاملة لفحص الأمراض المنقولة جنسيًا قبل الزواج في دبي."
                },
                {
                    "question": "هل يمكنني إجراء الفحص في فندقي؟",
                    "answer": "بالتأكيد. نقدم الفحص في أي موقع خاص في دبي بما في ذلك الفنادق، المنازل، والمكاتب."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}